import React from "react";
import { Provider } from "../../../../data/entities";
import { InfoBoxWrapper } from "../Styled";
import Section from "./Section";

interface ProviderInfoProps {
    provider?: Provider | null;
    providerName?: string;
    primaryInfoTitle?: string;
}

interface InfoDef {
    title: string;
    field: string;
    toValue?: (value: any, provider?: Provider | null) => string | number | undefined;
}

const contactFieldToValue = (field: string) => (value: any) => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const defaultContact = value?.find((item: any) => item.contact?.is_default)?.contact;
    const contact = defaultContact || value?.[0]?.contact;
    return contact?.[field];
};

const payableDaysToValue = (value: any) => {
    if (value === undefined || value === null || value === "") return undefined;
    return `${value} ngày`;
};

const SECTION_INFOS = [
    {
        title: "Thông tin nhà cung cấp",
        infoDefinitions: [
            { title: "Mã nhà cung cấp", field: "provider_code" },
            { title: "Số điện thoại", field: "phone" },
            { title: "Email", field: "email" },
            { title: "Fax", field: "fax" },
            { title: "Mã số thuế", field: "tax_id" },
            { title: "Địa chỉ", field: "address" },
        ],
    },
    {
        title: "Thông tin người liên hệ",
        infoDefinitions: [
            { title: "Người liên hệ", field: "contact_list", toValue: contactFieldToValue("name") },
            { title: "Email", field: "contact_list", toValue: contactFieldToValue("email") },
            { title: "Số điện thoại", field: "contact_list", toValue: contactFieldToValue("phone") },
            { title: "Cấp bậc", field: "contact_list", toValue: contactFieldToValue("position") },
        ],
    },
    {
        title: "Thanh toán",
        infoDefinitions: [
            { title: "Số ngày nợ", field: "payable_days", toValue: payableDaysToValue },
            { title: "Tên tài khoản", field: "bank_account_name" },
            { title: "Tên ngân hàng", field: "bank_name" },
            { title: "Số tài khoản", field: "bank_account_number" },
        ],
    },
];

const infoListFactory = (provider: Provider | null | undefined, infoList: InfoDef[]) => {
    if (!provider) return infoList.map((infoDef) => ({ title: infoDef.title, value: undefined }));

    return infoList.map((infoDef: InfoDef) => {
        const raw = (provider as any)[infoDef.field];
        const value = raw ? (infoDef.toValue ? infoDef.toValue(raw, provider) : raw) : undefined;
        return { title: infoDef.title, value };
    });
};

const getInitials = (name?: string) => {
    if (!name) return "NCC";
    const parts = name.trim().split(/\s+/);
    const initials = parts.length === 1 ? parts[0][0] : parts[0][0] + parts[parts.length - 1][0];
    return initials.toUpperCase();
};

const Header = ({ title, avatarBg }: { title: string; avatarBg: string }) => (
    <div className="tw-flex tw-items-center tw-gap-3">
        <div
            className={`tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full ${avatarBg} tw-text-black tw-font-semibold tw-text-base tw-ring-1 tw-ring-black/5`}
        >
            {getInitials(title)}
        </div>
        <div className="tw-text-xl tw-font-semibold tw-text-gray-900">{title}</div>
    </div>
);

const ProviderInfo = ({ provider, providerName, primaryInfoTitle }: ProviderInfoProps) => {
    const sectionInfos = primaryInfoTitle
        ? SECTION_INFOS.map((section, index) => index === 0 ? { ...section, title: primaryInfoTitle } : section)
        : SECTION_INFOS;

    if (!provider) {
        return (
            <InfoBoxWrapper>
                <Header title={providerName || "Không có thông tin nhà cung cấp"} avatarBg="tw-bg-amber-100" />
                <div className="tw-h-px tw-w-full tw-bg-secondary/60" />
                <div className="tw-text-gray-700 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3">
                    Đơn hàng này chưa có đủ thông tin nhà cung cấp. Hãy kiểm tra lại dữ liệu nhà cung cấp.
                </div>
            </InfoBoxWrapper>
        );
    }

    return (
            <InfoBoxWrapper>
                <Header title={provider.name} avatarBg="tw-bg-blue-100" />
                <div className="tw-h-px tw-w-full tw-bg-secondary/60" />
            {sectionInfos.map((sectionInfo: any, index: number) => (
                <Section
                    key={`provider-section-${index}`}
                    title={sectionInfo.title}
                    infoList={infoListFactory(provider, sectionInfo.infoDefinitions)}
                />
            ))}
        </InfoBoxWrapper>
    );
};

export default ProviderInfo;
